import { Box } from '@mui/material';

import mergeRefs from 'merge-refs';
import {
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useState,
    useLayoutEffect,
} from 'react';
import { APIManager, axios } from 'shared/manager';

import * as XLSX from 'xlsx';

import LinkService from 'shared/shim/react-pdf/LinkService';
import {
    NodeOrRenderer,
    ScrollPageIntoViewArgs,
} from 'shared/shim/react-pdf/shared/types';
import { useMeasure, useTimeoutFn, useUpdateEffect } from 'react-use';
import { Redacts } from 'shared/shim/react-pdf/PageCanvas';
import { Vector3 } from 'three';
import Message from 'shared/shim/react-pdf/Message';
import { DocumentPDF } from 'modules/home/manage-packets/solicitor-viewer/components/DocumentViewer';
import {
    csvArrayInfo,
    csvCell,
    csvCellInfo,
} from 'modules/home/manage-packets/solicitor-viewer/SolicitorDocumentPage';
import { AxiosError, CanceledError } from 'axios';
import { PageScrollTo } from 'shared/shim/react-pdf/Document';

import { Workbook, WorkbookInstance } from '@fortune-sheet/react';
import '@fortune-sheet/react/dist/index.css';
import FileSaver from 'file-saver';

export interface DocumentCSV extends DocumentPDF {
    resetSource: () => void;
    destroy?: () => void;
    setSheetZoom?: (number: any) => void;
    getExcelFile?: (filename: string) => void;
    pages: any;
}
interface PageViewportProps {
    height: number;
    scale: number;
    width: number;
}
export interface inferCSVRedact {
    getRedactData: () => Redacts[];
    setRedactData: (data: Redacts[] | null) => void;
    setRedactDataByIndex: (data: Redacts | null, index: number) => void;
    getRenderViewport: () => PageViewportProps;
    getTotalDrawnVertices: () => number;
    removeDrawnVertices: () => void;
    getViewport: () => PageViewportProps;
    scrollToSpecificRedact: (num: number) => void;
    scrollToSpecificVertices: (num: number) => void;
    scrollToSpecificCell: (row: number, column: number) => void;
    resetRenderer: () => void;
    reinitRenderer: () => void;
    disposeRenderer: () => void;
    getCellCSVData?: () => csvCellInfo[];
    getRangeCSVData?: () => csvArrayInfo[];
}
export interface loadSheet {
    numPages: number;
}
interface SheetViewer {
    onRefreshDocument: () => void;
    onScrolling: (value: boolean) => void;
    onLoadStart: () => void;
    onLoadCancelation: () => void;
    onLoadNetworkError: () => void;
    onSourceSuccess: () => void;
    onSourceError: () => void;
    onLoadSuccess: (sheet: loadSheet) => void;
    onErased?: (index: number) => void;
    onRemove?: (index: number) => void;
    onRenderSuccess: () => void;
    onChangeMeasure: () => void;
    onSheetChanged: (sheetForPage: number) => void;
    verticesData?: Redacts[];
    csvInfo: csvCellInfo[] | null;
    csvCellInfo?: csvCellInfo[];
    csvArrayInfo?: csvArrayInfo[];
    canvasRef?: React.Ref<HTMLCanvasElement>;
    canvasInnerRef?: React.Ref<HTMLCanvasElement>;
    redactRef: React.Ref<inferCSVRedact>;
    canDrawn: boolean;
    onDrawn?: (
        startpoint: THREE.Vector3,
        endpoint: THREE.Vector3,
        sheetNum: number
    ) => void;
    width: number;
    height: number;
    noData: NodeOrRenderer;
    loading: NodeOrRenderer;
    error: NodeOrRenderer;
    viewerId: any;
    file: string | null;
    className: string;
    inputRef: any;
    placeholder: string;
    ref: React.Ref<DocumentCSV>;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const FortuneSheetViewer: React.FC<SheetViewer> = forwardRef(
    ({ ...props }, ref) => {
        const {
            viewerId,
            file,
            onScrolling,
            onLoadStart,
            onSourceError,
            onSourceSuccess,
            onLoadSuccess,
            onLoadCancelation,
            onLoadNetworkError,
            csvInfo,
            csvCellInfo: csvDataCellInfo,
            csvArrayInfo: csvDataArrayInfo,
            onRenderSuccess,
            onSheetChanged,
            onChangeMeasure,

            canvasInnerRef,
            width,
            height,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            verticesData,
            inputRef,
            className,
            redactRef,
            noData,
            loading,
            error,
        } = props;

        const [enableViewer, setEnableViewer] = useState<boolean>(false);
        const [enableSheetData, setEnableSheetData] = useState<boolean>(false);
        const [data, setData] = useState<any>(null);
        const protocol = useRef<AbortController | null>(null);

        const canvasElement = useRef<HTMLCanvasElement | null>(null);

        const [arrayRedact, setRedacts] = useState<Redacts[]>([]);
        const [enableCanvas, setEnableCanvas] = useState<boolean>(false);
        const [currentSheetNum, setSheetNum] = useState<number>(0);
        const [isSheetLoaded, setSheetLoaded] = useState<boolean>(false);
        const [
            renderViewport,
            // setRenderViewport
        ] = useState<PageViewportProps>({ height: 0, scale: 0, width: 0 });

        const [viewport, setViewport] = useState<PageViewportProps>({
            height,
            scale: 1,
            width,
        });
        const [selectedCellHighlight, setSelectedCellHighlight] = useState<
            csvCellInfo[]
        >([]);
        const [selectedArrayHighlight, setSelectedArrayHighlight] = useState<
            csvArrayInfo[]
        >([]);

        const [arraySheet, setSheets] = useState<any[]>([]);
        const [, setLoadedTexture] = useState<boolean>(false);
        const viewerRef = useRef<HTMLDivElement | null>(null);
        const WorkBookRef = useRef<WorkbookInstance | null>(null);
        const [isNoData, setNoData] = useState<boolean>(true);
        const [isLoading, setLoading] = useState<boolean>(false);
        const [isError, setError] = useState<boolean>(false);
        const [sheetData, setSheetData] = useState<Array<any>>([]);
        const dummyLinkService = useRef<LinkService | null>(null);
        const [updateRenderSelector, setUpdateRenderSelector] =
            useState<boolean>(false);
        const dummyScrollPage = useRef<{
            scrollPageIntoView: (args: ScrollPageIntoViewArgs) => void;
            scrollTo: (args: PageScrollTo) => void;
        } | null>(null);

        const handleRangeSelectionToCell = useCallback(
            (array: csvArrayInfo[]): csvCell[] => {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                const RANGE = (x: number, y: number) =>
                    Array.from(
                        (function* () {
                            // eslint-disable-next-line no-plusplus, no-param-reassign
                            while (x <= y) yield x++;
                        })()
                    );
                const csvCell: csvCell[] = [];
                // eslint-disable-next-line no-plusplus
                for (let index = 0; index < array.length; index++) {
                    const e = array[index];
                    const column_range = RANGE(
                        e.column.at(0) ?? 0,
                        e.column.at(1) ?? 0
                    );
                    const row_range = RANGE(e.row.at(0) ?? 0, e.row.at(1) ?? 0);

                    // eslint-disable-next-line no-plusplus
                    for (let r = 0; r < column_range.length; r++) {
                        const c = column_range[r];
                        // eslint-disable-next-line no-plusplus
                        for (let d = 0; d < row_range.length; d++) {
                            const r = row_range[d];
                            csvCell.push({
                                row: r,
                                column: c,
                                Term: '',
                            });
                        }
                    }
                }

                return csvCell;
            },
            []
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [filteredCSVInfo, setFilteredCSVInfo] = useState<csvCellInfo[]>(
            []
        );

        const luckyCss: any = {
            margin: '0px',
            padding: '0px',
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: '0px',
            top: '0px',
        };
        const [
            measureRef,
            {
                x: measureX,
                y: measureY,
                width: measureWidth,
                height: measureHeight,
                top: measureTop,
                right: measureRight,
                bottom: measureBottom,
                left: measureLeft,
            },
        ] = useMeasure<HTMLDivElement>();
        useEffect(() => {
            if (typeof onChangeMeasure === 'function') {
                onChangeMeasure();
            }
        }, [
            measureX,
            measureY,
            measureWidth,
            measureHeight,
            measureTop,
            measureRight,
            measureBottom,
            measureLeft,
            onChangeMeasure,
        ]);

        const [
            isScrolling,
            //  setScrolling
        ] = useState<boolean>(false);
        // const [,] = useDebounce(
        //     () => {
        //         setScrolling(false);
        //     },
        //     2000,
        //     [isScrolling]
        // );
        // const handleScrolling = useCallback(() => {
        //     setScrolling(true);
        // }, []);
        useLayoutEffect(() => {
            if (typeof onScrolling === 'function') {
                onScrolling(isScrolling);
            }
        }, [isScrolling]);
        useLayoutEffect(() => {
            if (
                isLoading === false &&
                isError === false &&
                isNoData === false
            ) {
                setViewport({ width, height, scale: 1 });
                if (typeof onRenderSuccess === 'function') {
                    onRenderSuccess();
                }
                setUpdateRenderSelector(true);
                const rootCanvas: HTMLCanvasElement | null =
                    document.querySelector(`#${viewerId} canvas`);
                if (rootCanvas) {
                    canvasElement.current = rootCanvas;
                    mergeRefs(canvasElement, canvasInnerRef);
                }
            }
        }, [enableCanvas]);

        const handleErrorDetected = useCallback(() => {
            if (enableCanvas === false || isSheetLoaded === false) {
                setError(true);
            } else {
                setError(false);
            }
        }, [enableCanvas, isSheetLoaded]);
        const [, cancel, reset] = useTimeoutFn(handleErrorDetected, 5000);
        useEffect(() => {
            cancel();
        }, []);
        useLayoutEffect(() => {
            if (enableCanvas) reset();
        }, [enableCanvas]);
        const [isLoadError, setLoadError] = useState<boolean>(false);
        useLayoutEffect(() => {
            if (isLoadError) {
                if (enableCanvas === false || isSheetLoaded === false) {
                    setError(true);
                }
                setLoadError(false);
            }
        }, [isLoadError]);

        const handleSheetChange = useCallback(
            (sheet: any) => {
                const sheetIndex = arraySheet.findIndex(
                    (e) => e.name === sheet.name
                );
                setSheetNum(sheetIndex);
                setSheetLoaded(true);
                if (typeof onSheetChanged === 'function') {
                    onSheetChanged(sheetIndex);
                }
                setLoadedTexture(true);
            },
            [arraySheet, onSheetChanged]
        );

        const handleConvertToBorderInfo = useCallback((e: csvCellInfo) => {
            if (typeof e.row === 'number' && typeof e.column === 'number') {
                return {
                    rangeType: 'cell',
                    value: {
                        row_index: e.row,
                        col_index: e.column - 1,
                        l: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                        r: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                        t: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                        b: {
                            style: 10,
                            color: 'rgb(25, 30, 68)',
                        },
                    },
                };
            }
            return {
                rangeType: 'range',
                borderType: 'border-all',
                style: '3',
                color: 'rgb(25, 30, 68)',
                range: [
                    {
                        row: e.row,
                        column: Array.from(
                            typeof e.column !== 'number' ? e.column : []
                        ).map((x) => x - 1),
                    },
                ],
            };
        }, []);

        const isWhitespaceString = (str: string): boolean =>
            !str.replace(/\s/g, '').length;
        const handleConvertToConditionFormat = useCallback((e: csvCell) => {
            const { current: sheet } = WorkBookRef;
            const value = sheet?.getCellValue(e.row, e.column - 1);

            if (
                typeof value === 'string' &&
                (isWhitespaceString(value) || value === '')
            ) {
                return {
                    type: 'default',
                    cellrange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    format: {
                        textColor: '#000000',
                        cellColor: '#FFC808',
                    },
                    conditionName: 'duplicateValue',
                    conditionRange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    conditionValue: '1',
                };
            }
            if (typeof value === 'object' && value === null) {
                return {
                    type: 'default',
                    cellrange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    format: {
                        textColor: '#000000',
                        cellColor: '#FFC808',
                    },
                    conditionName: 'duplicateValue',
                    conditionRange: [
                        {
                            row: [e.row, e.row],
                            column: [e.column - 1, e.column - 1],
                        },
                    ],
                    conditionValue: '2',
                };
            }
            return {
                type: 'default',
                cellrange: [
                    {
                        row: [e.row, e.row],
                        column: [e.column - 1, e.column - 1],
                    },
                ],
                format: {
                    textColor: '#000000',
                    cellColor: '#FFC808',
                },
                conditionName: 'textContains',
                conditionRange: [
                    {
                        row: [e.row, e.row],
                        column: [e.column - 1, e.column - 1],
                    },
                ],
                conditionValue: [value],
            };
        }, []);

        useUpdateEffect(() => {
            const { current: sheet } = WorkBookRef;
            if (updateRenderSelector) {
                const generatesheetData = Array.from(sheetData).map((e) => ({
                    ...e,
                    config: {
                        borderInfo: Array.from(csvInfo || [])
                            .map(handleConvertToBorderInfo)
                            // .concat(
                            //     Array.from(csvDataArrayInfo || []).map(
                            //         handleConvertToBorderInfo
                            //     )
                            // )
                            // .concat(
                            //     Array.from(csvDataCellInfo || []).map(
                            //         handleConvertToBorderInfo
                            //     )
                            // )
                            .concat(
                                Array.from(selectedCellHighlight).map(
                                    handleConvertToBorderInfo
                                )
                            )
                            .concat(
                                Array.from(selectedArrayHighlight).map(
                                    handleConvertToBorderInfo
                                )
                            ),
                    },
                }));

                const sheetFile = sheet?.getAllSheets();

                if (sheetFile) {
                    const currentSheet = sheetFile[currentSheetNum];
                    if (currentSheet && currentSheet.config) {
                        const currentsheetData =
                            generatesheetData[currentSheetNum];
                        // currentSheet.config.borderInfo =
                        //     currentsheetData.config.borderInfo;
                        const updateSheet = Array.from(sheetFile).map((b) => ({
                            ...b,
                            config:
                                currentSheet.name === b.name
                                    ? {
                                          ...b.config,
                                          borderInfo:
                                              currentsheetData.config
                                                  .borderInfo,
                                      }
                                    : b.config,
                            luckysheet_conditionformat_save:
                                currentSheet.name === b.name
                                    ? Array.from(
                                          (filteredCSVInfo as csvCell[]) || []
                                      )
                                          .map(handleConvertToConditionFormat)
                                          // .concat(
                                          //     Array.from(
                                          //         handleRangeSelectionToCell(csvDataArrayInfo || [])
                                          //     ).map(handleConvertToConditionFormat)
                                          // )
                                          // .concat(
                                          //     Array.from((csvDataCellInfo as csvCell[]) || []).map(
                                          //         handleConvertToConditionFormat
                                          //     )
                                          // )
                                          .concat(
                                              Array.from(
                                                  selectedCellHighlight as csvCell[]
                                              ).map(
                                                  handleConvertToConditionFormat
                                              )
                                          )
                                          .concat(
                                              Array.from(
                                                  handleRangeSelectionToCell(
                                                      selectedArrayHighlight
                                                  )
                                              ).map(
                                                  handleConvertToConditionFormat
                                              )
                                          )
                                    : [],
                        }));

                        sheet?.updateSheet(updateSheet);

                        // luckysheet.buildGridData(currentsheetData);
                        // luckysheet.sheetmanage.mergeCalculation(currentsheetData.index);
                        // luckysheet.sheetmanage.setSheetParam();
                        // luckysheet.sheetmanage.showSheet();
                        // luckysheet.sheetmanage.restoreCache();
                        // formula.execFunctionGroupForce(
                        //     luckysheetConfigsetting.forceCalculation
                        // );
                        // console.log('test', currentSheet);
                        // sheetFile[currentSheetNum] = currentSheet;
                        // sheet?.updateSheet(sheetFile);
                        // luckysheet.refreshFormula();
                        // luckysheet.jfrefreshgrid();
                        // luckysheet.luckysheetrefreshgrid(width, height);
                        setUpdateRenderSelector(false);
                    }
                }
            }
        }, [updateRenderSelector]);
        const [updateRangeSelect, setUpdateRangeSelect] =
            useState<boolean>(false);
        const [updateSelector, setSelector] = useState<any>(null);

        useUpdateEffect(() => {
            if (updateRangeSelect && updateSelector) {
                const selector = updateSelector;

                if (enableCanvas) {
                    if (
                        selector.column.join(',') !==
                            `${selector.column[0]},${selector.column[0]}` ||
                        selector.row.join(',') !==
                            `${selector.row[0]},${selector.row[0]}`
                    ) {
                        const arrColumn = Array.from(selector.column).map(
                            (x: any) => x + 1
                        );
                        const arrRow = selector.row;
                        const column = arrColumn.join(',');
                        const row = arrRow.join(',');

                        if (
                            selectedArrayHighlight.some(
                                (e) =>
                                    e.column.join(',') === column &&
                                    e.row.join(',') === row
                            )
                        ) {
                            setSelectedArrayHighlight((prev) => {
                                const updatedHiglight = prev;

                                return updatedHiglight.filter(
                                    (e) =>
                                        e.column.join(',') !== column &&
                                        e.row.join(',') !== row
                                );
                            });
                        } else {
                            setSelectedArrayHighlight((prev) => {
                                const updatedHiglight = prev;
                                updatedHiglight.push({
                                    row: arrRow,
                                    column: arrColumn,
                                    Term: '',
                                });
                                return updatedHiglight;
                            });
                        }
                        // range
                    } else {
                        // single cell

                        const column = selector.column[0];
                        const row = selector.row[0];

                        if (
                            selectedCellHighlight.some(
                                (e) =>
                                    typeof e.column === 'number' &&
                                    e.column - 1 === column &&
                                    e.row === row
                            )
                        ) {
                            setSelectedCellHighlight((prev) => {
                                const updatedHiglight = prev;

                                const highlightIndex =
                                    updatedHiglight.findIndex(
                                        (e) =>
                                            typeof e.column === 'number' &&
                                            e.column - 1 === column &&
                                            e.row === row
                                    );
                                return updatedHiglight.filter(
                                    (x, i) => i !== highlightIndex
                                );
                            });
                            // have selected highlight;
                        } else {
                            setSelectedCellHighlight((prev) => {
                                const updatedHiglight = prev;
                                updatedHiglight.push({
                                    row,
                                    column: column + 1,
                                    Term: '',
                                });
                                return updatedHiglight;
                            });
                            // not having selected highlight
                        }
                    }
                }
                setSelector(null);
                setUpdateRenderSelector(true);
                setUpdateRangeSelect(false);
            }
        }, [updateRangeSelect]);
        // const handleRangeSelected = useCallback(() => {
        //     if (updateSelector) {
        //         setUpdateRangeSelect(true);
        //     }
        // }, [updateSelector]);
        const handleRangeSelect = useCallback((sheet: any, selector: any) => {
            setSelector(selector);
            // setUpdateRangeSelect(true);
        }, []);
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const alphaVal = (s: string) => s.toLowerCase().charCodeAt(0) - 97;
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const loadCellData = (data: object[]) =>
            Array.from({ length: data.length })
                .map((b: any, i) => {
                    const beta: any = data[i];
                    return Object.keys(beta).map((c) => ({
                        r: i,
                        c: alphaVal(c),
                        v: beta[c],
                    }));
                })
                .flat();

        const loadExcel = useCallback(
            (
                sheets: any[],
                cellInfo?: csvCellInfo[],
                arrayInfo?: csvArrayInfo[]
            ) => {
                const updatedArrayhighlight = selectedArrayHighlight.concat(
                    arrayInfo || []
                );
                const updatedCellhighlight = selectedCellHighlight.concat(
                    cellInfo || []
                );
                const SheetBordered = filteredCSVInfo
                    ? Array.from(sheets).map((e) => ({
                          ...e,
                          zoomRatio: 0.9, // 缩放比例
                          celldata: loadCellData(e.data),
                          config: {
                              borderInfo: Array.from(filteredCSVInfo)
                                  .map(handleConvertToBorderInfo)
                                  .concat(
                                      Array.from(
                                          updatedArrayhighlight || []
                                      ).map(handleConvertToBorderInfo)
                                  )
                                  .concat(
                                      Array.from(
                                          updatedCellhighlight || []
                                      ).map(handleConvertToBorderInfo)
                                  ),
                              authority: {
                                  selectLockedCells: 1,
                                  selectunLockedCells: 1,
                                  formatCells: 0,
                                  formatColumns: 0,
                                  formatRows: 0,
                                  insertColumns: 0,
                                  insertRows: 0,
                                  insertHyperlinks: 0,
                                  deleteColumns: 0,
                                  deleteRows: 0,
                                  sort: 1,
                                  filter: 1,
                                  usePivotTablereports: 0,
                                  editObjects: 0,
                                  editScenarios: 0,
                                  sheet: 1,
                              },
                          },
                      }))
                    : Array.from(sheets).map((e) => ({
                          ...e,
                          zoomRatio: 0.9, // 缩放比例
                          celldata: loadCellData(e.data),
                          config: {
                              borderInfo: Array.from(
                                  updatedArrayhighlight || []
                              )
                                  .map(handleConvertToBorderInfo)
                                  .concat(
                                      Array.from(
                                          updatedCellhighlight || []
                                      ).map(handleConvertToBorderInfo)
                                  ),
                              authority: {
                                  selectLockedCells: 1,
                                  selectunLockedCells: 1,
                                  formatCells: 0,
                                  formatColumns: 0,
                                  formatRows: 0,
                                  insertColumns: 0,
                                  insertRows: 0,
                                  insertHyperlinks: 0,
                                  deleteColumns: 0,
                                  deleteRows: 0,
                                  sort: 1,
                                  filter: 1,
                                  usePivotTablereports: 0,
                                  editObjects: 0,
                                  editScenarios: 0,
                                  sheet: 1,
                              },
                          },
                      }));

                setSheets(SheetBordered);
                setSheetData(SheetBordered);
                setSelectedCellHighlight(updatedCellhighlight);
                setSelectedArrayHighlight(updatedArrayhighlight);

                if (typeof onLoadSuccess === 'function') {
                    onLoadSuccess({ numPages: sheets.length });
                }
                setEnableSheetData(true);
                setLoading(false);
            },
            [
                filteredCSVInfo,
                handleConvertToBorderInfo,
                loadCellData,
                onLoadSuccess,
                selectedArrayHighlight,
                selectedCellHighlight,
            ]
        );
        useImperativeHandle(
            ref,
            () => ({
                linkService: dummyLinkService,
                viewer: dummyScrollPage,
                resetSource: () => {},
                pages: undefined,
                destroy: () => {
                    setEnableViewer(false);
                    setEnableSheetData(false);
                    // const { luckysheet } = window;
                    // luckysheet.setConfig({
                    //     allowUpdate: false,
                    //     updateUrl: false,
                    //     data: [],
                    //     hook: null,
                    // });

                    // luckysheet.closeWebsocket();

                    // luckysheet.destroy();
                },
                getExcelFile: (fileName) => {
                    FileSaver.saveAs(data, fileName);
                },
                setSheetZoom: (num) => {
                    const { current: fortuneSheet } = WorkBookRef;
                    const sheets = fortuneSheet?.getAllSheets();
                    fortuneSheet?.updateSheet(
                        Array.from(sheets ?? []).map((d) => ({
                            ...d,
                            zoomRatio: num + 0.8, // 缩放比例
                        }))
                    );
                },
            }),
            [data]
        );
        useImperativeHandle(
            redactRef,
            () => ({
                getCellCSVData: () => selectedCellHighlight,
                getRangeCSVData: () => selectedArrayHighlight,
                disposeRenderer: () => {},
                resetRenderer: () => {
                    setEnableCanvas(false);
                },
                getSheetData: () => {},
                reinitRenderer: () => {},
                /**
                 * Get current active drawn vertices data
                 * @return {Redacts[]} array of redact object
                 */
                getRedactData: () => arrayRedact,
                /**
                 * Set current active drawn vertices data
                 */
                setRedactData: (data: Redacts[] | null) => {
                    if (data) {
                        setRedacts(arrayRedact.concat(data));
                    } else {
                        setRedacts([]);
                    }
                },
                /**
                 * Set current active drawn vertices data by index
                 */
                setRedactDataByIndex: (data: Redacts | null, index: number) => {
                    if (data) {
                        setRedacts(
                            Array.from(arrayRedact).map((o, i) => {
                                if (i === index) {
                                    return data;
                                }
                                return o;
                            })
                        );
                    } else {
                        setRedacts(arrayRedact.filter((o, i) => i !== index));
                    }
                },
                removeDrawnVertices: () => {
                    setRedacts([]);
                },
                /**
                 * Get total drawn count
                 * @return {number} length of the stored vertices in array
                 */
                getTotalDrawnVertices: () => arrayRedact.length,
                /**
                 * Get renderer viewport
                 * @return {PageViewport} page viewport with association to display pixel density
                 */
                getRenderViewport: () => renderViewport,
                /**
                 * Get renderer viewport
                 * @return {PageViewport} page viewport with association to window size
                 */
                getViewport: () => viewport,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                scrollToSpecificRedact: (num: number) => {},
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                scrollToSpecificVertices: (num: number) => {},
                scrollToSpecificCell: (row: number, column: number) => {
                    const { current: sheet } = WorkBookRef;
                    if (sheet) {
                        sheet.scroll({
                            targetRow: row,
                            targetColumn: column,
                        });
                    }
                },
            }),
            [
                arrayRedact,
                renderViewport,
                selectedArrayHighlight,
                selectedCellHighlight,
                viewport,
            ]
        );

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const readExcelContent = useCallback(
            (file: Blob) => {
                const reader = new FileReader();

                reader.onload = (e) => {
                    if (typeof onLoadStart === 'function') {
                        onLoadStart();
                    }
                    if (e.target) {
                        const workbook = XLSX.read(e.target.result, {
                            type: 'binary',
                            cellFormula: true,
                            cellNF: true,
                            cellText: true,
                            dateNF: 'dd-MMM-yyy', // Specif
                        });
                        // eslint-disable-next-line no-underscore-dangle
                        const _sheetsData: any[] = [];
                        const sheets: any[] = [];
                        workbook.SheetNames.forEach((sheetName, i) => {
                            // eslint-disable-next-line no-underscore-dangle
                            const _sheetData: any[] = XLSX.utils.sheet_to_json(
                                workbook.Sheets[sheetName],
                                {
                                    raw: false,
                                    rawNumbers: false,
                                    header: 'A',
                                    dateNF: 'dd-MMM-yyy', // Specify the date format string here
                                }
                            );

                            sheets.push({
                                name: sheetName,
                                color: '',
                                status: '1',
                                order: i,
                                data: _sheetData,
                                // config: {},
                                id: i,
                            });
                            _sheetsData.push({
                                name: sheetName,
                                color: '',
                                status: '1',
                                order: i,
                                data: _sheetData,
                                config: {},
                                id: i,
                            });
                        });

                        loadExcel(sheets, csvDataCellInfo, csvDataArrayInfo);
                    }
                };
                reader.readAsBinaryString(file);
            },
            [csvDataArrayInfo, csvDataCellInfo, loadExcel, onLoadStart]
        );

        const handleAbortProtocol = useCallback(() => {
            if (protocol.current) {
                protocol.current.abort();
            }
        }, [protocol]);
        useEffect(
            () => () => {
                handleAbortProtocol();
            },
            []
        );
        useEffect(() => {
            if (file) {
                setEnableCanvas(false);
                setEnableViewer(true);
                setLoading(true);
                handleAbortProtocol();
                const controller = new AbortController();
                protocol.current = controller;
                APIManager.sendGet(
                    file,
                    false,
                    {},
                    { responseType: 'blob', signal: controller.signal },
                    axios
                )
                    .then((response) => {
                        readExcelContent(response.data);
                        if (typeof onSourceSuccess === 'function') {
                            onSourceSuccess();
                        }
                        setData(response.data);
                        setNoData(false);
                    })
                    .catch((error) => {
                        if (error instanceof CanceledError === false) {
                            if (
                                error !== null &&
                                error instanceof AxiosError &&
                                error.code === 'ERR_NETWORK'
                            ) {
                                if (typeof onLoadNetworkError === 'function') {
                                    onLoadNetworkError();
                                }

                                setLoadError(true);
                            }

                            if (typeof onSourceError === 'function') {
                                onSourceError();
                            }
                            setLoading(false);
                            setNoData(true);
                        } else if (
                            error instanceof CanceledError === true &&
                            typeof onLoadCancelation === 'function'
                        ) {
                            onLoadCancelation();
                        }
                    });
            }
        }, [file]);

        return (
            <Box
                sx={{ width: '100%', height: '100%' }}
                className={`sheetViewer ${className}`}
                ref={measureRef}
            >
                {!file && isNoData && (
                    <Message type="no-data">
                        {typeof noData === 'function' ? noData() : noData}
                    </Message>
                )}
                {isLoading && (
                    <Message type="loading">
                        {typeof loading === 'function' ? loading() : loading}
                    </Message>
                )}

                {isError && (
                    <Message type="error">
                        {typeof error === 'function' ? error() : error}
                    </Message>
                )}

                {enableViewer && (
                    <Box
                        ref={mergeRefs(viewerRef, inputRef)}
                        id={viewerId}
                        style={luckyCss}
                    >
                        {enableSheetData && (
                            <Workbook
                                ref={WorkBookRef}
                                data={[...arraySheet]}
                                // onOp={(op) => console.log('any', op)}
                                // onChange={(data) => console.log('data', data)}
                                hooks={{
                                    // scroll: handleScrolling,
                                    // afterUpdateCell: handleUpdatedCanvasExcel,
                                    // resized: handleResizeComponent,
                                    // afterAddSheet: () => setEnableCanvas(true),

                                    // beforePaste: handleRangeSelected,
                                    afterSelectionChange: handleRangeSelect,

                                    // beforeActivateSheet: handleSheetBefore,
                                    afterAddSheet: handleSheetChange,
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        );
    }
);

export default FortuneSheetViewer;
